import type { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { TrendChart } from '../../../media/breakdown/TrendChart.js';
import { ErrorState } from '../../../media/shared/ErrorState.js';
import type { CreativeScoreComponentName } from '../../data/creativeScore.js';
import {
  creativeScoreComponentLabel,
  orderedScoreComponents,
  scoreTier,
} from '../../data/creativeScore.js';
import type { TrendGranularity } from '../../data/format.js';
import { bucketLabel, formatMetricValue, rangeLabel } from '../../data/format.js';
import { CREATIVE_SCORE_COMPONENT_ACCENT } from '../../data/presentation.js';
import { accentColor } from '../../MetricTile.js';
import type {
  CreativeDetailScope,
  ScoreOutput,
  ScoreSeries,
  ScoreSeriesName,
} from '../data/api.js';
import { useSingleCreativeScore } from '../data/api.js';
import { ChartSection } from '../shared/ChartSection.js';
import { SectionSkeleton } from '../shared/SectionSkeleton.js';
import { TrendMetricPicker } from '../shared/TrendMetricPicker.js';
import { ChartLegend } from './ChartLegend.js';

const MAX_TICKS = 7;

export type ScoreSeriesSelection = [ScoreSeriesName] | [ScoreSeriesName, ScoreSeriesName];

interface ScoreOverTimeSectionProps {
  scope: CreativeDetailScope;
  creativeId: string;
  leadingKpiLabel: string;
  seriesNames: ScoreSeriesSelection;
  onSeriesNamesChange: (seriesNames: ScoreSeriesSelection) => void;
}

function isScoreComponentName(name: string): name is CreativeScoreComponentName {
  return name in CREATIVE_SCORE_COMPONENT_ACCENT;
}

function isSelectableSeriesName(name: string): name is ScoreSeriesName {
  return name === 'score' || isScoreComponentName(name);
}

function toSeriesSelection(names: [string] | [string, string]): ScoreSeriesSelection {
  const [first, second] = names.filter(isSelectableSeriesName);
  return first !== undefined && second !== undefined ? [first, second] : [first ?? 'score'];
}

function findSeries(data: ScoreOutput, name: ScoreSeriesName): ScoreSeries | undefined {
  return data.series.find((series) => series.name === name);
}

function hasValues(series: ScoreSeries | undefined): series is ScoreSeries {
  return series?.points.some((point) => point.value !== null) ?? false;
}

function bucketAxis(
  points: { date: string }[],
  granularity: TrendGranularity,
): {
  xAxisData: number[];
  xAxisTickFormatter: (bucket: string | number) => string;
  xAxisValueFormatter: (bucket: string | number) => string;
} {
  const buckets = points.map((_point, index) => index + 1);
  const tickStep = Math.max(1, Math.ceil(buckets.length / MAX_TICKS));
  const dateForBucket = (bucket: number): string | undefined => points[bucket - 1]?.date;

  return {
    xAxisData: buckets,
    xAxisTickFormatter: (bucket) => {
      const labelled = (Number(bucket) - 1) % tickStep === 0 || bucket === buckets.length;
      if (!labelled) return '';
      const date = dateForBucket(Number(bucket));
      return date === undefined ? '' : bucketLabel(date, granularity);
    },
    xAxisValueFormatter: (bucket) => {
      const date = dateForBucket(Number(bucket));
      return date === undefined ? `#${bucket}` : bucketLabel(date, granularity);
    },
  };
}

function latestScoreValue(score: ScoreOutput): number | undefined {
  const points = findSeries(score, 'score')?.points ?? [];
  for (let index = points.length - 1; index >= 0; index -= 1) {
    const value = points[index]?.value;
    if (value !== null && value !== undefined) return value;
  }
  return undefined;
}

function seriesColorFor(theme: Theme, name: ScoreSeriesName, score: ScoreOutput): string {
  if (name === 'score') {
    const latest = latestScoreValue(score);
    return latest === undefined
      ? theme.palette.text.disabled
      : theme.palette.score[scoreTier(latest)].main;
  }
  if (isScoreComponentName(name)) return accentColor(theme, CREATIVE_SCORE_COMPONENT_ACCENT[name]);
  return theme.palette.text.disabled;
}

function ScoreChartBody({
  score,
  seriesNames,
}: {
  score: ScoreOutput;
  seriesNames: ScoreSeriesSelection;
}): ReactNode {
  const theme = useTheme();

  const pickedSeries = seriesNames
    .map((name) => findSeries(score, name))
    .filter((series): series is ScoreSeries => series !== undefined);
  const firstSeries = pickedSeries[0];
  if (firstSeries === undefined) return <SectionSkeleton variant="chart" />;

  const spendSeries = findSeries(score, 'spend');
  const includeSpend = hasValues(spendSeries);
  const { granularity, currencySymbol } = score;
  const { xAxisData, xAxisTickFormatter, xAxisValueFormatter } = bucketAxis(
    firstSeries.points,
    granularity,
  );

  const chartSeries = [
    ...pickedSeries.map((series) => ({
      id: series.name,
      label: series.label,
      values: series.points.map((point) => point.value),
      valueFormatter: (value: number) => String(Math.round(value)),
      color: seriesColorFor(theme, series.name, score),
      yAxisId: 'score',
    })),
    ...(includeSpend && spendSeries !== undefined
      ? [
          {
            id: spendSeries.name,
            label: spendSeries.label,
            values: spendSeries.points.map((point) => point.value),
            valueFormatter: (value: number) =>
              formatMetricValue(value, 'compact_currency', currencySymbol),
            color: theme.palette.score.medium.main,
            dashed: true,
            yAxisId: 'spend',
          },
        ]
      : []),
  ];

  return (
    <>
      <TrendChart
        xAxisData={xAxisData}
        xAxisTickFormatter={xAxisTickFormatter}
        xAxisValueFormatter={xAxisValueFormatter}
        series={chartSeries}
      />
      <ChartLegend
        entries={chartSeries.map((series) => ({
          id: series.id,
          label: series.label,
          color: series.color,
        }))}
      />
    </>
  );
}

export function ScoreOverTimeSection({
  scope,
  creativeId,
  leadingKpiLabel,
  seriesNames,
  onSeriesNamesChange,
}: ScoreOverTimeSectionProps): ReactNode {
  const score = useSingleCreativeScore({ ...scope, creativeId }, true);

  const componentLabels = creativeScoreComponentLabel(leadingKpiLabel);
  const metricLabel = (name: string): string => {
    if (name === 'score') return 'Overall score';
    return isScoreComponentName(name) ? componentLabels[name] : name;
  };
  const availableMetrics =
    score.data === undefined
      ? ['score']
      : ['score', ...orderedScoreComponents(score.data.components).map(([name]) => name)];

  return (
    <ChartSection
      variant="card"
      title="Score Trend"
      subtitle={rangeLabel(scope.dateRange)}
      actions={
        <TrendMetricPicker
          label="Scores"
          availableMetrics={availableMetrics}
          metricNames={seriesNames}
          onMetricNamesChange={(names) => onSeriesNamesChange(toSeriesSelection(names))}
          metricLabel={metricLabel}
        />
      }
    >
      {score.isError ? (
        <ErrorState />
      ) : score.data === undefined ? (
        <SectionSkeleton variant="chart" />
      ) : (
        <ScoreChartBody score={score.data} seriesNames={seriesNames} />
      )}
    </ChartSection>
  );
}
